import { useNavigate } from 'react-router-dom';

interface DetailHeaderProps {
  name: string;
  meta: string;
  /** Render the name in italic + medium weight when it's a fallback label. */
  nameItalic?: boolean;
  /** Optional status pill rendered to the right of the name. */
  pillLabel?: string;
  /** Tailwind classes for the status pill (light + dark). */
  pillClass?: string;
  /** Optional override; defaults to history back, falling back to conversations list. */
  onBack?: () => void;
  /** Slug used to compute the conversations list fallback when there's no history. */
  fallbackSlug?: string;
  /**
   * Drip-state indicator (bug 5, 2026-06-02).
   *   undefined → hide pill entirely (legacy / pre-load state)
   *   true      → green tappable "Drip on" pill (tap = stop)
   *   false     → grey static "Drip off" pill (informational only)
   */
  dripActive?: boolean;
  /**
   * SMS_QuoteFixes STEP 2 (2026-07-17) — a drip enroll DEFERRED to the 9am
   * window. The tags aren't written yet, so dripActive is false, but the
   * enroll is real and queued. When present (and dripActive is false), the
   * pill reads "Drip queued · <label>" instead of "Drip off". Pre-formatted
   * send-time label, e.g. "9:31 AM".
   */
  dripQueuedLabel?: string | null;
  /** Called when the green "Drip on" pill is tapped. */
  onStopDrip?: () => void;
  /** Disable interaction + show pending UI during mutation. */
  dripBusy?: boolean;
}

const PILL_BASE =
  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-tight';

/**
 * Sticky white header with back chevron + contact name + status pill +
 * meta line. R11 consult upgrade 2026-05-06: shows a status pill so the
 * owner immediately knows what state the conversation is in.
 */
export function DetailHeader({
  name,
  meta,
  nameItalic,
  pillLabel,
  pillClass,
  onBack,
  fallbackSlug,
  dripActive,
  dripQueuedLabel,
  onStopDrip,
  dripBusy,
}: DetailHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else if (fallbackSlug) {
      navigate(`/${fallbackSlug}/conversations`);
    } else {
      navigate('/');
    }
  };

  return (
    <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-4 pb-3 pt-3">
      <button
        type="button"
        onClick={handleBack}
        aria-label="Back"
        className="flex h-8 w-8 items-center justify-center text-ss-blue"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={[
              'min-w-0 truncate text-base text-ink-900 dark:text-[#E2E8F0]',
              nameItalic ? 'font-medium italic' : 'font-bold',
            ].join(' ')}
          >
            {name}
          </div>
          {pillLabel ? (
            <span className={[PILL_BASE, pillClass ?? ''].join(' ')}>{pillLabel}</span>
          ) : null}
          {dripActive === true ? (
            <button
              type="button"
              onClick={onStopDrip}
              disabled={dripBusy === true}
              title="Drip is active — tap to halt the automated follow-up workflow (Shelby SMS replies will continue)."
              className={[
                PILL_BASE,
                'gap-1 ss-press',
                'bg-emerald-100 text-emerald-800 border border-emerald-200',
                'dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800/60',
                'disabled:opacity-60',
                'hover:bg-emerald-200 dark:hover:bg-emerald-900/60',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
              />
              {dripBusy ? 'Stopping…' : 'Drip on'}
            </button>
          ) : dripActive === false && dripQueuedLabel ? (
            // STEP 2 (2026-07-17) — enroll deferred to the 9am window: the tags
            // aren't written yet so dripActive is false, but the drip IS on its
            // way. Don't say "off" for a queued lead (live: Kevin P, 7/17).
            <span
              title={`Drip enrollment is queued — sends ${dripQueuedLabel}. The follow-up tags are written when the send window opens.`}
              className={[
                PILL_BASE,
                'gap-1',
                'bg-amber-100 text-amber-800 border border-amber-200',
                'dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800/60',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
              />
              Drip queued · {dripQueuedLabel}
            </span>
          ) : dripActive === false ? (
            <span
              title="No active drip on this contact."
              className={[
                PILL_BASE,
                'gap-1',
                'bg-ink-100 text-ink-500 border border-ink-200',
                'dark:bg-[#1E293B] dark:text-[#94A3B8] dark:border-[#334155]',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full bg-ink-400 dark:bg-[#64748B]"
              />
              Drip off
            </span>
          ) : null}
        </div>
        {meta ? (
          <div className="mt-0.5 truncate text-xs text-ink-500 dark:text-[#94A3B8]">{meta}</div>
        ) : null}
      </div>
    </header>
  );
}
